import * as d3 from 'd3';
import { PieArcDatum } from 'd3';
import { useEffect, useRef } from 'react';
import { Stat } from '../../api';
import { getDate, defaults } from './utils';

function processData(
  data: Stat,
  offset: number,
  daysInMonth: number,
  day: number,
  getDomains: (data: Stat) => Record<string, Array<number>>,
): Record<string, number> {
  let start: number, end: number;
  if (day) {
    start = 23 + offset + day - 1;
    end = start + 24;
  } else {
    start = 23 + offset;
    end = 23 + offset + daysInMonth * 24;
  }
  const domains: Record<string, Array<number>> = getDomains(data);
  const result: Record<string, number> = {};
  for (const domain in domains) {
    result[domain] = 0;
    for (let index = start; index < end; index++) {
      if (domains[domain][index]) result[domain] += domains[domain][index];
    }
  }
  return result;
}

export default function MonthPieChart({
  stat,
  offset,
  daysInMonth,
  day,
  month,
  year,
  tz,
  options,
  getDomains,
  getTitle,
}: {
  stat: Stat;
  offset: number;
  daysInMonth: number;
  day: number;
  month: number;
  year: number;
  tz: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: any;
  getDomains: (data: Stat) => Record<string, Array<number>>;
  getTitle: () => string;
}) {
  options = Object.assign(defaults, options);
  const ref = useRef<SVGSVGElement>(null);
  useEffect(() => {
    const svg = d3.select(ref.current);
    const _data: Record<string, number> = processData(stat, offset, daysInMonth, day, getDomains);
    const margin = options.margin;
    const innerWidth = options.width - margin.left - margin.right;
    const innerHeight = options.width / options.aspectRatio - margin.top - margin.bottom;
    const radius: number = Math.min(innerHeight, innerWidth) / 2;
    svg.selectAll('*').remove();
    svg
      .attr('width', options.width)
      .attr('height', options.width / options.aspectRatio)
      .style('display', 'block')
      .style('margin', 'auto');
    svg
      .append('g')
      .append('text')
      .text(getTitle())
      .attr('transform', `translate(${options.width}, 20)`)
      .style('text-anchor', 'end')
      .style('font-weight', 'bold')
      .style('font-family', 'arial');
    svg
      .append('g')
      .append('text')
      .text(getDate(day, month, year, tz))
      .attr('transform', `translate(${options.width}, 35)`)
      .style('text-anchor', 'end')
      .style('font-family', 'arial')
      .style('font-size', '10px');
    const g = svg
      .append('g')
      .attr('transform', `translate(${options.width / 2},${options.width / options.aspectRatio / 2})`);
    g.append('g').attr('class', 'slices');
    g.append('g').attr('class', 'labels');
    g.append('g').attr('class', 'lines');
    const color = d3.scaleOrdinal<string>().domain(Object.keys(_data)).range(d3.schemeSet2);
    const pie = d3.pie<{ key: string; value: number }>().value((datum: { key: string; value: number }) => datum.value);
    const data_ready = pie(
      Object.entries(_data).map((item) => {
        return { key: item[0], value: item[1] };
      }),
    );
    const arc = d3
      .arc<PieArcDatum<{ key: string; value: number }>>()
      .innerRadius(0.4 * radius)
      .outerRadius(0.8 * radius);
    const outerArc = d3
      .arc<PieArcDatum<{ key: string; value: number }>>()
      .innerRadius(0.9 * radius)
      .outerRadius(0.9 * radius);

    g.select('.slices')
      .selectAll('path.slice')
      .data(data_ready)
      .enter()
      .insert('path')
      .attr('d', arc)
      .attr('fill', (datum: d3.PieArcDatum<{ key: string; value: number }>): string => color(datum.data.key))
      .attr('stroke', 'black')
      .style('stroke-width', '2px')
      .style('opacity', 0.7);

    const midAngle = (datum: d3.PieArcDatum<{ key: string; value: number }>): number =>
      datum.startAngle + (datum.endAngle - datum.startAngle) / 2;

    g.select('.labels')
      .selectAll('text')
      .data(data_ready)
      .enter()
      .append('text')
      .attr('dy', '.35em')
      .style('font-family', 'arial')
      .style('font-size', '12px')
      .attr('transform', (datum: d3.PieArcDatum<{ key: string; value: number }>): string => {
        const pos = outerArc.centroid(datum);
        pos[0] = radius * (midAngle(datum) < Math.PI ? 1 : -1);
        return `translate(${pos})`;
      })
      .style('text-anchor', (datum: d3.PieArcDatum<{ key: string; value: number }>) =>
        midAngle(datum) < Math.PI ? 'start' : 'end',
      )
      .text((datum: d3.PieArcDatum<{ key: string; value: number }>): string => datum.data.key);

    g.select('.lines')
      .selectAll('polyline')
      .data(data_ready)
      .enter()
      .append('polyline')
      .attr('points', (datum: d3.PieArcDatum<{ key: string; value: number }>): string => {
        const pos = outerArc.centroid(datum);
        pos[0] = radius * 0.95 * (midAngle(datum) < Math.PI ? 1 : -1);
        return [arc.centroid(datum), outerArc.centroid(datum), pos].toString();
      })
      .style('fill', 'none')
      .style('stroke', 'black')
      .style('stroke-width', '2px')
      .style('opacity', '0.3');
  }, [
    day,
    daysInMonth,
    getDomains,
    getTitle,
    month,
    offset,
    options.aspectRatio,
    options.margin,
    options.width,
    stat,
    tz,

    year,
  ]);
  return <svg ref={ref}></svg>;
}
